







import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

@Component
export default class DownloadButton extends Vue {
  @Prop({ required: true })
  public readonly data!: any[];

  @Prop({ default: 'Download Data' })
  public readonly label!: string;

  @Prop({ default: 'data-export' })
  public readonly fileNamePrefix!: string;

  @Prop({ default: () => [] })
  public readonly headers!: string[];

  @Prop({ default: () => ({}) })
  public readonly headerMapping!: Record<string, string>;

  @Prop({ default: 'fa-solid fa-cloud-download' })
  public readonly icon!: string;

  @Prop({ default: 'tw-text-primary-300 hover:tw-text-primary-400' })
  public readonly iconClass!: string;

  @Prop({ default: 'tw-text-black' })
  public readonly textClass!: string;

  public async downloadData(): Promise<void> {
    if (this.data.length === 0) {
      this.$emit('download-error', 'No data to download');
      return;
    }

    try {
      // Convert data to CSV
      const csvContent = this.convertToCSV(this.data);

      // Download the CSV file
      this.downloadCsvFile(csvContent, `${this.fileNamePrefix}-${Date.now()}`);

      // Emit event for tracking or additional actions
      this.$emit('download-complete');
    } catch (error) {
      console.error('Error downloading data:', error);
      this.$emit('download-error', error);
    }
  }

  private downloadCsvFile(csvContent: string, fileName: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private convertToCSV(data: any[]): string {
    if (data.length === 0) return '';

    // Determine headers to use
    let csvHeaders: string[];

    if (this.headers && this.headers.length > 0) {
      // Use provided headers
      csvHeaders = this.headers;
    } else {
      // Extract headers from the first item
      csvHeaders = Object.keys(data[0]);
    }

    // Map header display names if provided
    const headerRow = csvHeaders.map((header) => this.headerMapping[header] || header);

    // Create CSV header row
    let csv = headerRow.join(',') + '\n';

    // Add data rows
    data.forEach((item) => {
      const row = csvHeaders.map((header) => {
        const value = item[header];

        // Handle different types of values
        let cellValue: string;

        if (Array.isArray(value)) {
          // Format arrays more readably
          try {
            // For arrays of objects, try to format them more nicely
            const formattedItems = value.map((item) => {
              if (typeof item === 'object' && item !== null) {
                // Format each object in the array
                return this.formatObjectForCSV(item);
              }
              return String(item);
            });
            cellValue = formattedItems.join('; ');
          } catch (e) {
            // Fallback to simple join if formatting fails
            cellValue = value.join('; ');
          }
        } else if (typeof value === 'object' && value !== null) {
          // Format objects more readably
          cellValue = this.formatObjectForCSV(value);
        } else {
          // Convert primitives to strings
          cellValue = value !== undefined && value !== null ? String(value) : '';
        }

        // Escape quotes and wrap in quotes
        return `"${cellValue.replace(/"/g, '""')}"`;
      });

      csv += row.join(',') + '\n';
    });

    return csv;
  }

  private formatObjectForCSV(obj: any): string {
    try {
      // Handle special cases for known object types
      if (obj.__typename) {
        // GraphQL objects often have a __typename field
        const type = obj.__typename;

        // Remove __typename from the formatted output
        const { __typename, ...rest } = obj;

        // Format based on the type
        if (type.includes('Action')) {
          // Format actions specially
          return this.formatAction(obj);
        }
      }

      // Default formatting: convert to a readable key-value format
      const pairs = Object.entries(obj)
        .filter(([key, value]) => key !== '__typename' && value !== null && value !== undefined)
        .map(([key, value]) => {
          // Format the value based on its type
          let formattedValue: string;

          if (Array.isArray(value)) {
            formattedValue = `[${value.length} items]`;
          } else if (typeof value === 'object' && value !== null) {
            formattedValue = '{...}';
          } else {
            formattedValue = String(value);
          }

          return `${key}: ${formattedValue}`;
        });

      return pairs.join(', ');
    } catch (e) {
      // Fallback to JSON if custom formatting fails
      return JSON.stringify(obj);
    }
  }

  private formatAction(action: any): string {
    if (!action) return '';

    try {
      const type = action.type || '';
      const ignoreFailPricing =
        action.ignoreFailPricing !== undefined ? `ignoreFailPricing: ${action.ignoreFailPricing}` : '';

      // Format based on action type
      if (action.lines && Array.isArray(action.lines)) {
        // Flatten the lines data
        const linesData = action.lines.map((line: any, index: number) => {
          const lineDetails = [];

          // Add all available fields from the line
          if (line.valueExtractor) lineDetails.push(`valueExtractor: ${line.valueExtractor}`);
          if (line.assetExtractor) lineDetails.push(`assetExtractor: ${line.assetExtractor}`);
          if (line.lineQualifierExtractor) lineDetails.push(`lineQualifier: ${line.lineQualifierExtractor}`);
          if (line.contactId) lineDetails.push(`contactId: ${line.contactId}`);
          if (line.categoryId) lineDetails.push(`categoryId: ${line.categoryId}`);

          // Handle metadataIds which might be null, array, or something else
          if (line.metadataIds) {
            if (Array.isArray(line.metadataIds)) {
              if (line.metadataIds.length > 0) {
                lineDetails.push(`metadataIds: [${line.metadataIds.join(', ')}]`);
              } else {
                lineDetails.push('metadataIds: []');
              }
            } else {
              lineDetails.push(`metadataIds: ${line.metadataIds}`);
            }
          } else {
            lineDetails.push('metadataIds: null');
          }

          return `Line ${index + 1}: {${lineDetails.join(', ')}}`;
        });

        // Combine all line details with the action type and other properties
        const result = [`Type: ${type}`];
        if (ignoreFailPricing) result.push(ignoreFailPricing);
        return result.concat(linesData).join(' | ');
      }

      // If no lines, just return the basic info
      const result = [`Type: ${type}`];
      if (ignoreFailPricing) result.push(ignoreFailPricing);
      return result.join(' | ');
    } catch (e) {
      return JSON.stringify(action);
    }
  }
}
